/** Post-apply summary for a completed sync_aux_info task: outcome rollup +
 *  the first receipts (full list stays on the review page / task artifact).
 *
 *  A receipt that created or updated a tag carries its `tag_id`, so the
 *  panel is the shortest path from "the sync ran" to the tag it wrote. */
import { Alert, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import * as auxSyncApi from '@/api/auxSync';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { receiptTagActions } from '@/features/bgtasks/taskLinks';

const OUTCOME_COLOR: Record<string, 'success' | 'info' | 'warning' | 'error' | 'default'> = {
  created: 'success',
  restored: 'success',
  updated: 'info',
  unchanged: 'default',
  skipped: 'warning',
  error: 'error',
};

export function AuxSyncResultPanel({
  taskId,
  tenantId = null,
}: {
  taskId: string;
  /** The task's tenant — scopes the "bookings with this tag" hop. */
  tenantId?: number | null;
}) {
  const query = useQuery({
    queryKey: ['aux-sync', 'result', taskId],
    queryFn: () => auxSyncApi.applyResult(taskId, { page_size: 20 }),
    staleTime: Infinity,
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      return status !== 409 && status !== 404 && failureCount < 2;
    },
  });
  if (query.isLoading) return <CircularProgress size={20} />;
  if (query.isError) {
    return (
      <Typography variant="body2" color="text.secondary">
        No apply results (the task finished without materializing).
      </Typography>
    );
  }
  const data = query.data!;
  const rollup = data.rollup;
  const outcomes = ['created', 'restored', 'updated', 'unchanged', 'skipped', 'error'].filter(
    (o) => Number(rollup[o] ?? 0) > 0,
  );
  const errors = data.results.filter((r) => r.outcome === 'error');
  const tagged = data.results.filter((r) => r.tag_id != null);
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
        {Boolean(rollup.dry_run) && <Chip size="small" color="warning" label="dry run" />}
        <Chip size="small" variant="outlined" label={`${rollup.selected ?? 0} selected`} />
        {outcomes.map((o) => (
          <Chip
            key={o}
            size="small"
            color={OUTCOME_COLOR[o] ?? 'default'}
            variant="outlined"
            label={`${rollup[o]} ${o}`}
          />
        ))}
      </Stack>
      {errors.length > 0 && (
        <Alert severity="warning">
          {errors.length} record(s) failed to apply, e.g. {errors[0]?.key}: {errors[0]?.detail}
        </Alert>
      )}
      {tagged.length > 0 && (
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
          <Typography variant="caption" color="text.secondary">
            Tags written:
          </Typography>
          {tagged.map((r) => (
            <ValueActions key={r.key} chip actions={receiptTagActions(r.tag_id!, { tenantId })}>
              <Chip size="small" variant="outlined" label={r.key} />
            </ValueActions>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
